import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Col, Container, Form, FormGroup, Input, Label, Row, Spinner } from "reactstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { signInWithEmailAndPassword } from "firebase/auth";
import neoLogo from "../../assets/images/Neo_Logo_C.svg";
import bgImage from "../../assets/images/bgneo.png";
import { auth, functions } from "../../services/firebase";
import { LANDING_PLAN_CATALOG } from "./planCatalog";

// ── Mercado Pago Brick (mesma lógica do CheckoutPage) ──
const PAYMENT_METHODS = [
    { code: "PIX",    label: "PIX",    icon: "mdi-qrcode-scan",       accent: "#00f3ff", description: "Pagamento instantaneo com QR Code ou copia e cola." },
    { code: "CARD",   label: "Cartao", icon: "mdi-credit-card-outline", accent: "#5cf7d4", description: "Checkout protegido do Mercado Pago com cartao de credito ou debito." },
    { code: "BOLETO", label: "Boleto", icon: "mdi-barcode",           accent: "#f59e0b", description: "Emissao de boleto com compensacao conforme o banco." },
];

const MERCADO_PAGO_SDK_URL = "https://sdk.mercadopago.com/js/v2";

const loadMercadoPagoSdk = () => new Promise((resolve, reject) => {
    if (window.MercadoPago) { resolve(window.MercadoPago); return; }
    const existingScript = document.querySelector(`script[src="${MERCADO_PAGO_SDK_URL}"]`);
    if (existingScript) {
        existingScript.addEventListener("load", () => resolve(window.MercadoPago));
        existingScript.addEventListener("error", () => reject(new Error("Nao foi possivel carregar o SDK do Mercado Pago.")));
        return;
    }
    const script = document.createElement("script");
    script.src = MERCADO_PAGO_SDK_URL;
    script.async = true;
    script.onload = () => resolve(window.MercadoPago);
    script.onerror = () => reject(new Error("Nao foi possivel carregar o SDK do Mercado Pago."));
    document.body.appendChild(script);
});

const getBrickPaymentMethods = (selectedMethod, installments) => {
    const normalized = String(selectedMethod || "PIX").toUpperCase();
    const activeMethods = { maxInstallments: installments || 1 };
    if (normalized === "CARD")   { activeMethods.creditCard = "all"; activeMethods.debitCard = "all"; }
    if (normalized === "PIX")    { activeMethods.bankTransfer = "all"; }
    if (normalized === "BOLETO") { activeMethods.ticket = "all"; }
    return activeMethods;
};

/**
 * ReactivatePage
 * 
 * Fluxo de reativação de conta inativa/expirada.
 * Segue exatamente o mesmo padrão visual e de integração do CheckoutPage.
 *
 * Etapas:
 * 1. Login (confirmar identidade com senha existente)
 * 2. Seleção de plano + Pagamento via Mercado Pago (Brick transparente)
 * 3. Confirmação / Resultado do pagamento
 */
const ReactivatePage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const incomingState = location.state || {};
    const {
        tenantId: initialTenantId,
        tenantName: initialTenantName,
        email: initialEmail,
        planId: initialPlanId,
        billingStatus: initialBillingStatus
    } = incomingState;

    // ── Estado Global ──
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState(initialEmail || "");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    // Dados do Tenant resolvidos após login
    const [tenantId, setTenantId] = useState(initialTenantId || "");
    const [tenantName, setTenantName] = useState(initialTenantName || "");
    const [currentPlanId, setCurrentPlanId] = useState(initialPlanId || "");
    const [billingStatus, setBillingStatus] = useState(initialBillingStatus || "");

    // Plano + Pagamento
    const [selectedPlanCode, setSelectedPlanCode] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("PIX");
    const [brickLoading, setBrickLoading] = useState(false);
    const [brickError, setBrickError] = useState("");
    const [paymentResult, setPaymentResult] = useState(null);
    const brickControllerRef = useRef(null);
    const brickContainerId = "reactivate-payment-brick";
    const mercadoPagoPublicKey = process.env.REACT_APP_MERCADO_PAGO_PUBLIC_KEY || "";

    // Plano selecionado a partir do catálogo
    const resolvedPlan = useMemo(() => {
        if (!selectedPlanCode) return null;
        return LANDING_PLAN_CATALOG.find(p => p.planId === selectedPlanCode || p.code === selectedPlanCode) || null;
    }, [selectedPlanCode]);

    useEffect(() => {
        document.title = "Reativar Conta | NeoApp";
        window.scrollTo(0, 0);
    }, []);

    // ══════════════════════════════════════════════════════════════
    // ETAPA 1: Autenticação
    // ══════════════════════════════════════════════════════════════
    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMessage("");

        try {
            await signInWithEmailAndPassword(auth, email.trim(), password);

            const checkEmailExists = httpsCallable(functions, "checkEmailExists");
            const response = await checkEmailExists({ email: email.trim() });
            const data = response.data || {};

            if (data.context === "active") {
                setSuccessMessage("Sua conta ja esta ativa! Redirecionando...");
                setTimeout(() => navigate("/dashboard"), 1500);
                return;
            }

            if (data.context === "blocked_user") {
                await auth.signOut();
                setErrorMessage("Sua conta foi bloqueada pelo administrador. Contate o suporte.");
                return;
            }

            setTenantId(data.tenantId || "");
            setTenantName(data.tenantName || "Sua Empresa");
            setCurrentPlanId(data.planId || "");
            setBillingStatus(data.billingStatus || "draft");
            setStep(2);
        } catch (err) {
            if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
                setErrorMessage("Senha incorreta. Tente novamente.");
            } else if (err.code === "auth/user-not-found") {
                setErrorMessage("Nenhuma conta encontrada com este e-mail.");
            } else {
                setErrorMessage(err.message || "Erro ao autenticar.");
            }
        } finally {
            setLoading(false);
        }
    };

    // ══════════════════════════════════════════════════════════════
    // ETAPA 2: Montar Brick de Pagamento (idêntico ao CheckoutPage)
    // ══════════════════════════════════════════════════════════════
    useEffect(() => {
        if (step !== 3 || !resolvedPlan || !tenantId) return undefined;
        if (!mercadoPagoPublicKey) {
            setBrickError("Defina REACT_APP_MERCADO_PAGO_PUBLIC_KEY para renderizar o checkout transparente.");
            return undefined;
        }

        let cancelled = false;

        const mountBrick = async () => {
            setBrickLoading(true);
            setBrickError("");

            try {
                await loadMercadoPagoSdk();
                if (cancelled) return;

                if (brickControllerRef.current?.unmount) {
                    await brickControllerRef.current.unmount();
                }

                const mp = new window.MercadoPago(mercadoPagoPublicKey, { locale: "pt-BR" });
                const bricksBuilder = mp.bricks();

                const container = document.getElementById(brickContainerId);
                if (container) container.innerHTML = "";

                brickControllerRef.current = await bricksBuilder.create("payment", brickContainerId, {
                    initialization: {
                        amount: Number(resolvedPlan.amount),
                        payer: { email: email.trim(), entityType: "individual" },
                    },
                    customization: {
                        visual: { style: { theme: "dark" } },
                        paymentMethods: getBrickPaymentMethods(paymentMethod, resolvedPlan.installments),
                    },
                    callbacks: {
                        onReady: () => { if (!cancelled) setBrickLoading(false); },
                        onSubmit: async (brickState) => {
                            setErrorMessage("");
                            setSuccessMessage("");
                            setPaymentResult(null);

                            try {
                                const processPayment = httpsCallable(functions, "processPublicCheckoutPayment");
                                const response = await processPayment({
                                    idTenant: tenantId,
                                    planCode: resolvedPlan.code,
                                    formData: brickState?.formData || brickState || {},
                                    originUrl: window.location.origin,
                                });

                                const payment = response.data?.payment || null;
                                setPaymentResult(payment);

                                if (payment?.status === "approved") {
                                    setSuccessMessage("Pagamento aprovado! Sua conta foi reativada.");
                                    // Força refresh do token
                                    const user = auth.currentUser;
                                    if (user) await user.getIdToken(true);
                                    setStep(4); // Tela de sucesso
                                } else if (payment?.status) {
                                    setSuccessMessage("Pagamento enviado. Confira abaixo as instrucoes.");
                                }

                                return response.data;
                            } catch (error) {
                                setErrorMessage(error.message || "Erro ao processar o pagamento.");
                                throw error;
                            }
                        },
                        onError: (error) => {
                            if (!cancelled) {
                                setBrickLoading(false);
                                setBrickError("Nao foi possivel carregar o formulario do Mercado Pago.");
                            }
                        },
                    },
                });
            } catch (error) {
                if (!cancelled) {
                    setBrickError(error.message || "Erro ao iniciar o checkout transparente.");
                    setBrickLoading(false);
                }
            }
        };

        mountBrick();
        return () => {
            cancelled = true;
            if (brickControllerRef.current?.unmount) {
                brickControllerRef.current.unmount().catch(() => {});
            }
        };
    }, [step, tenantId, resolvedPlan, paymentMethod, mercadoPagoPublicKey, email]);

    // Planos disponíveis (sem trial)
    const availablePlans = LANDING_PLAN_CATALOG.filter(p => !p.isTrial && p.amount > 0);

    const priceLabel = resolvedPlan
        ? `R$ ${resolvedPlan.price}${resolvedPlan.periodLabel || ""}`
        : "";

    return (
        <div
            className="landing-page-container"
            style={{
                minHeight: "100vh",
                backgroundColor: "#0f1118",
                backgroundImage: `linear-gradient(135deg, rgba(6, 8, 14, 0.95) 0%, rgba(15, 17, 24, 0.88) 45%, rgba(8, 25, 35, 0.82) 100%), url(${bgImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                overflowX: "hidden",
            }}
        >
            <Container fluid className="px-4 px-xl-5 py-4 py-lg-5">
                <div className="d-flex justify-content-between align-items-center mb-4 mb-lg-5">
                    <Link to="/">
                        <img src={neoLogo} alt="NeoApp" height="36" style={{ filter: "brightness(0) invert(1)" }} />
                    </Link>
                    <Link to="/login" className="text-decoration-none" style={{ color: "#dbe4ff", textTransform: "uppercase", letterSpacing: "1px", fontSize: "0.8rem" }}>
                        Voltar para login
                    </Link>
                </div>

                <Row className="g-4 align-items-stretch">
                    <Col lg={7} xl={6}>
                        <div className="checkout-shell h-100">
                            <div className="checkout-shell-outline">REATIVAR</div>

                            <div className="d-flex flex-wrap align-items-center gap-3 mb-4">
                                <div className="checkout-step-chip">{`Etapa ${Math.min(step, 3)} de 3`}</div>
                                <div style={{ color: "#8d9ab9", fontSize: "0.92rem" }}>
                                    {step === 1 ? "Confirme sua identidade" : step === 2 ? "Selecione o plano" : step === 3 ? "Pagamento seguro" : "Conta reativada"}
                                </div>
                            </div>

                            {errorMessage && <Alert color="danger" fade={false}>{errorMessage}</Alert>}
                            {successMessage && <Alert color="success" fade={false}>{successMessage}</Alert>}

                            {/* ═══ ETAPA 1: LOGIN ═══ */}
                            {step === 1 && (
                                <>
                                    <h1 className="checkout-title">Reative sua conta</h1>
                                    <p className="checkout-subtitle">
                                        Detectamos que voce ja possui uma conta no NeoApp.
                                        Confirme sua identidade para reativar sua assinatura.
                                    </p>

                                    <Form onSubmit={handleLogin}>
                                        <Row>
                                            <Col md={12}>
                                                <FormGroup className="mb-4">
                                                    <Label className="checkout-label">E-mail</Label>
                                                    <Input className="checkout-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                                                </FormGroup>
                                            </Col>
                                            <Col md={12}>
                                                <FormGroup className="mb-4">
                                                    <Label className="checkout-label">Senha</Label>
                                                    <Input className="checkout-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        <div className="d-flex flex-wrap gap-3">
                                            <Link to="/forgot-password" className="checkout-secondary-btn text-decoration-none d-flex align-items-center">
                                                Esqueci minha senha
                                            </Link>
                                            <button type="submit" className="checkout-primary-btn flex-grow-1" disabled={loading}>
                                                {loading ? <Spinner size="sm" /> : "Confirmar identidade"}
                                            </button>
                                        </div>
                                    </Form>
                                </>
                            )}

                            {/* ═══ ETAPA 2: SELEÇÃO DE PLANO ═══ */}
                            {step === 2 && (
                                <>
                                    <h1 className="checkout-title">Escolha seu plano</h1>
                                    <p className="checkout-subtitle">
                                        A empresa <strong style={{ color: "#00f3ff" }}>{tenantName}</strong> esta com a
                                        assinatura {billingStatus === "expired" ? "expirada" : "pendente"}.
                                        {currentPlanId ? ` Plano anterior: ${currentPlanId.toUpperCase()}.` : ""}
                                        {" "}Selecione um plano para continuar ao pagamento.
                                    </p>

                                    <div className="payment-method-grid mb-4">
                                        {availablePlans.map((plan) => (
                                            <button
                                                key={plan.code}
                                                type="button"
                                                className={`payment-method-card ${selectedPlanCode === plan.planId ? "active" : ""}`}
                                                onClick={() => setSelectedPlanCode(plan.planId)}
                                            >
                                                <i className="mdi mdi-shield-star-outline" style={{ color: plan.highlighted ? "#00f3ff" : "#5cf7d4" }} />
                                                <strong>{plan.title}</strong>
                                                <span>R$ {plan.price}{plan.periodLabel || ""}</span>
                                            </button>
                                        ))}
                                    </div>

                                    <div className="d-flex flex-wrap gap-3">
                                        <button type="button" className="checkout-secondary-btn" onClick={() => setStep(1)}>
                                            Voltar
                                        </button>
                                        <button
                                            type="button"
                                            className="checkout-primary-btn flex-grow-1"
                                            disabled={!selectedPlanCode}
                                            onClick={() => setStep(3)}
                                        >
                                            Continuar para pagamento
                                        </button>
                                    </div>
                                </>
                            )}

                            {/* ═══ ETAPA 3: PAGAMENTO (Brick do Mercado Pago) ═══ */}
                            {step === 3 && resolvedPlan && (
                                <div>
                                    <h1 className="checkout-title">Finalize o pagamento</h1>
                                    <p className="checkout-subtitle">
                                        O pagamento acontece em ambiente seguro do Mercado Pago, sem armazenar dados sensiveis no NeoApp.
                                    </p>

                                    <div className="checkout-summary-card mb-4">
                                        <div>
                                            <span>Plano selecionado</span>
                                            <strong>{resolvedPlan.title}</strong>
                                            <p>{resolvedPlan.headline}</p>
                                        </div>
                                        <div className="text-lg-end">
                                            <div className="checkout-price">{priceLabel}</div>
                                            <small>Checkout transparente Mercado Pago</small>
                                        </div>
                                    </div>

                                    <Label className="checkout-label mb-3">Forma de pagamento</Label>
                                    <div className="payment-method-grid mb-4">
                                        {PAYMENT_METHODS.map((method) => (
                                            <button
                                                key={method.code}
                                                type="button"
                                                className={`payment-method-card ${paymentMethod === method.code ? "active" : ""}`}
                                                onClick={() => setPaymentMethod(method.code)}
                                            >
                                                <i className={`mdi ${method.icon}`} style={{ color: method.accent }} />
                                                <strong>{method.label}</strong>
                                                <span>{method.description}</span>
                                            </button>
                                        ))}
                                    </div>

                                    <div className="checkout-note mb-4">
                                        <i className="mdi mdi-shield-check-outline" />
                                        <div>
                                            <strong>Pagamento sem redirecionamento</strong>
                                            <p className="mb-0">
                                                O formulario abaixo e renderizado pelo Mercado Pago dentro desta pagina e envia o pagamento direto para o backend do NeoApp.
                                            </p>
                                        </div>
                                    </div>

                                    {brickError && <Alert color="danger" fade={false}>{brickError}</Alert>}

                                    <div className="checkout-brick-shell mb-4">
                                        {brickLoading && (
                                            <div className="text-center py-4">
                                                <Spinner color="light" />
                                                <p className="mb-0 mt-3" style={{ color: "#b9c2d8" }}>
                                                    Carregando formulario seguro do Mercado Pago...
                                                </p>
                                            </div>
                                        )}
                                        <div id={brickContainerId} />
                                    </div>

                                    {paymentResult && (
                                        <div className="checkout-payment-result mb-4">
                                            <span>Status da cobranca</span>
                                            <strong>{paymentResult.status || "Pendente"}</strong>
                                            {paymentResult.qr_code_base64 && (
                                                <img
                                                    src={`data:image/png;base64,${paymentResult.qr_code_base64}`}
                                                    alt="QR Code PIX"
                                                    style={{ width: "220px", maxWidth: "100%", borderRadius: "16px", margin: "18px 0" }}
                                                />
                                            )}
                                            {paymentResult.qr_code && (
                                                <div className="checkout-code-box">{paymentResult.qr_code}</div>
                                            )}
                                            {paymentResult.ticket_url && (
                                                <a href={paymentResult.ticket_url} target="_blank" rel="noreferrer" className="checkout-secondary-btn text-decoration-none mt-3">
                                                    Abrir boleto
                                                </a>
                                            )}
                                        </div>
                                    )}

                                    <div className="d-flex flex-wrap gap-3">
                                        <button type="button" className="checkout-secondary-btn" onClick={() => setStep(2)}>
                                            Voltar aos planos
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* ═══ ETAPA 4: SUCESSO ═══ */}
                            {step === 4 && (
                                <div className="text-center py-4">
                                    <div style={{ fontSize: "5rem", color: "#5cf7d4", animation: "reactivate-pop 0.5s ease" }} className="mb-4">
                                        <i className="mdi mdi-check-circle" />
                                    </div>
                                    <h1 className="checkout-title">Conta reativada!</h1>
                                    <p className="checkout-subtitle mx-auto" style={{ maxWidth: "500px" }}>
                                        A empresa <strong style={{ color: "#00f3ff" }}>{tenantName}</strong> esta
                                        novamente ativa. Todos os seus dados, alunos e configuracoes foram preservados.
                                    </p>

                                    <button type="button" className="checkout-primary-btn mt-3" onClick={() => navigate("/dashboard")}>
                                        <i className="mdi mdi-arrow-right me-2" />
                                        Acessar o painel
                                    </button>
                                </div>
                            )}
                        </div>
                    </Col>

                    {/* ── Painel Lateral (mesmo padrão do CheckoutPage) ── */}
                    <Col lg={5} xl={6}>
                        <div className="checkout-aside h-100">
                            <div className="checkout-plan-badge">Reativacao de conta</div>

                            <h2 className="checkout-aside-title">Seus dados estao salvos</h2>
                            <p className="checkout-aside-copy">
                                Ao reativar, voce recupera o acesso completo a todos os modulos, alunos,
                                turmas e historico financeiro da sua empresa. Nenhum dado e perdido.
                            </p>

                            {resolvedPlan && (
                                <div className="checkout-aside-price">{priceLabel}</div>
                            )}

                            <div className="checkout-feature-list">
                                {[
                                    { text: "Alunos e matriculas preservados", active: true },
                                    { text: "Historico financeiro intacto", active: true },
                                    { text: "Turmas e grade mantidas", active: true },
                                    { text: "Configuracoes e cargos salvos", active: true },
                                    { text: "Logs de auditoria acessiveis", active: true },
                                ].map((feature) => (
                                    <div key={feature.text} className="checkout-feature-item">
                                        <i className={`mdi ${feature.active ? "mdi-check-circle" : "mdi-close-circle-outline"}`} />
                                        <span>{feature.text}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="checkout-support-card">
                                <span>Precisa de ajuda?</span>
                                <strong>Fale com o suporte pelo WhatsApp</strong>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>

            <style>
                {`
                    .checkout-shell,
                    .checkout-aside {
                        position: relative;
                        border-radius: 30px;
                        border: 1px solid rgba(255, 255, 255, 0.08);
                        background: rgba(16, 19, 29, 0.84);
                        backdrop-filter: blur(18px);
                        box-shadow: 0 30px 90px rgba(0, 0, 0, 0.35);
                        overflow: hidden;
                    }

                    .checkout-shell {
                        padding: clamp(24px, 3vw, 40px);
                    }

                    .checkout-shell-outline {
                        position: absolute;
                        right: 16px;
                        top: 12px;
                        color: transparent;
                        -webkit-text-stroke: 1px rgba(0, 243, 255, 0.12);
                        font-size: clamp(3.8rem, 7vw, 7rem);
                        font-weight: 800;
                        letter-spacing: 4px;
                        pointer-events: none;
                    }

                    .checkout-step-chip {
                        background: rgba(0, 243, 255, 0.1);
                        color: #00f3ff;
                        padding: 8px 14px;
                        border-radius: 999px;
                        text-transform: uppercase;
                        letter-spacing: 1.1px;
                        font-weight: 700;
                        font-size: 0.75rem;
                    }

                    .checkout-title {
                        color: #ffffff;
                        font-size: clamp(2.1rem, 4vw, 3.3rem);
                        font-weight: 700;
                        line-height: 1.1;
                        margin-bottom: 14px;
                        letter-spacing: -1px;
                    }

                    .checkout-subtitle {
                        color: #b9c2d8;
                        font-size: 1rem;
                        line-height: 1.8;
                        margin-bottom: 32px;
                        max-width: 720px;
                    }

                    .checkout-label {
                        color: #00f3ff;
                        font-size: 0.8rem;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        font-weight: 700;
                        margin-bottom: 10px;
                    }

                    .checkout-input {
                        min-height: 54px;
                        border-radius: 14px;
                        background: rgba(7, 10, 18, 0.5) !important;
                        border: 1px solid rgba(255, 255, 255, 0.1) !important;
                        color: #ffffff !important;
                        padding: 14px 16px !important;
                    }

                    .checkout-input:focus {
                        border-color: rgba(0, 243, 255, 0.9) !important;
                        box-shadow: 0 0 0 3px rgba(0, 243, 255, 0.12) !important;
                    }

                    .checkout-primary-btn,
                    .checkout-secondary-btn {
                        min-height: 56px;
                        border-radius: 16px;
                        padding: 0 22px;
                        border: 1px solid transparent;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        gap: 10px;
                        font-weight: 700;
                        letter-spacing: 0.4px;
                        transition: all 0.2s ease;
                    }

                    .checkout-primary-btn {
                        background: linear-gradient(135deg, #00f3ff 0%, #5cf7d4 100%);
                        color: #07131f;
                    }

                    .checkout-secondary-btn {
                        background: transparent;
                        color: #ffffff;
                        border-color: rgba(255, 255, 255, 0.14);
                    }

                    .checkout-primary-btn:hover,
                    .checkout-secondary-btn:hover,
                    .payment-method-card:hover {
                        transform: translateY(-1px);
                    }

                    .checkout-summary-card,
                    .checkout-support-card,
                    .checkout-note,
                    .checkout-payment-result,
                    .checkout-brick-shell {
                        background: rgba(255, 255, 255, 0.03);
                        border: 1px solid rgba(255, 255, 255, 0.08);
                        border-radius: 20px;
                    }

                    .checkout-summary-card {
                        display: flex;
                        justify-content: space-between;
                        gap: 18px;
                        padding: 20px 24px;
                    }

                    .checkout-support-card {
                        padding: 20px 24px;
                    }

                    .checkout-summary-card span,
                    .checkout-support-card span {
                        display: block;
                        color: #8d9ab9;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        font-size: 0.75rem;
                        margin-bottom: 8px;
                    }

                    .checkout-summary-card strong,
                    .checkout-support-card strong {
                        color: #ffffff;
                        display: block;
                        font-size: 1.1rem;
                        margin-bottom: 8px;
                    }

                    .checkout-summary-card p,
                    .checkout-support-card p {
                        color: #b9c2d8;
                        line-height: 1.6;
                    }

                    .checkout-price,
                    .checkout-aside-price {
                        color: #00f3ff;
                        font-size: clamp(1.8rem, 4vw, 2.6rem);
                        font-weight: 700;
                    }

                    .payment-method-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
                        gap: 14px;
                    }

                    .payment-method-card {
                        background: rgba(255, 255, 255, 0.03);
                        border: 1px solid rgba(255, 255, 255, 0.08);
                        border-radius: 18px;
                        min-height: 146px;
                        text-align: left;
                        padding: 18px;
                        color: #ffffff;
                        transition: all 0.2s ease;
                    }

                    .payment-method-card.active {
                        border-color: rgba(0, 243, 255, 0.9);
                        box-shadow: 0 0 0 3px rgba(0, 243, 255, 0.12);
                        background: rgba(0, 243, 255, 0.07);
                    }

                    .payment-method-card i {
                        display: block;
                        font-size: 1.7rem;
                        margin-bottom: 12px;
                    }

                    .payment-method-card strong {
                        display: block;
                        font-size: 1rem;
                        margin-bottom: 8px;
                    }

                    .payment-method-card span {
                        display: block;
                        color: #b9c2d8;
                        font-size: 0.88rem;
                        line-height: 1.6;
                    }

                    .checkout-note {
                        display: flex;
                        gap: 14px;
                        align-items: flex-start;
                        padding: 18px 20px;
                    }

                    .checkout-note i {
                        color: #22c55e;
                        font-size: 1.5rem;
                    }

                    .checkout-note strong {
                        color: #ffffff;
                        display: block;
                        margin-bottom: 6px;
                    }

                    .checkout-note p {
                        color: #b9c2d8;
                        line-height: 1.6;
                    }

                    .checkout-brick-shell {
                        padding: 20px;
                    }

                    .checkout-payment-result {
                        padding: 20px;
                    }

                    .checkout-payment-result span {
                        display: block;
                        color: #8d9ab9;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        font-size: 0.75rem;
                        margin-bottom: 10px;
                    }

                    .checkout-payment-result strong {
                        color: #ffffff;
                        font-size: 1.15rem;
                    }

                    .checkout-code-box {
                        margin-top: 12px;
                        padding: 14px;
                        border-radius: 14px;
                        background: rgba(7, 10, 18, 0.55);
                        color: #dffbff;
                        font-size: 0.88rem;
                        line-height: 1.7;
                        word-break: break-all;
                    }

                    .checkout-aside {
                        padding: clamp(24px, 3vw, 38px);
                        display: flex;
                        flex-direction: column;
                        justify-content: space-between;
                    }

                    .checkout-plan-badge {
                        display: inline-flex;
                        align-items: center;
                        width: fit-content;
                        background: rgba(255, 255, 255, 0.06);
                        color: #00f3ff;
                        padding: 9px 14px;
                        border-radius: 999px;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        font-size: 0.74rem;
                        margin-bottom: 22px;
                    }

                    .checkout-aside-title {
                        color: #ffffff;
                        font-size: clamp(2rem, 4vw, 3rem);
                        font-weight: 700;
                        margin-bottom: 12px;
                    }

                    .checkout-aside-copy {
                        color: #d0d8eb;
                        font-size: 1rem;
                        line-height: 1.8;
                        margin-bottom: 24px;
                    }

                    .checkout-feature-list {
                        display: grid;
                        gap: 12px;
                        margin: 28px 0;
                    }

                    .checkout-feature-item {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        color: #e8eefb;
                        line-height: 1.6;
                    }

                    .checkout-feature-item i {
                        color: #00f3ff;
                        font-size: 1.1rem;
                    }

                    @media (max-width: 767px) {
                        .checkout-summary-card {
                            flex-direction: column;
                        }
                    }

                    @keyframes reactivate-pop {
                        0% { transform: scale(0); opacity: 0; }
                        50% { transform: scale(1.2); }
                        100% { transform: scale(1); opacity: 1; }
                    }
                `}
            </style>
        </div>
    );
};

export default ReactivatePage;
